import { color } from "minimum-ui/tokens";
import styled from "styled-components";

import { IconButton } from "../IconButton/IconButton";
import { Spinner } from "../Spinner/Spinner";
import { Typography } from "../Typography/Typography";

export enum Keys {
  cancel = "cancel",
  delete = "delete",
  download = "download",
  previewModal = "previewModal",
  previewNewTab = "previewNewTab",
  replace = "replace",
  view = "view",
}

export interface FileInfoProps {
  children?: React.ReactNode;
  name: string;
  description?: string | React.ReactNode;
  disabled?: boolean;
  statusLabel?: string;
  isLoading?: boolean;
  isReplacing?: boolean;
  isError?: boolean;
  isWarning?: boolean;
  url?: string;
  actions?: Partial<Record<Keys, () => void>>;
  icons?: Partial<Record<Keys, boolean>>;
  tooltips?: Partial<Record<Keys, string>>;
  infoText?: string;
}

export const FileInfo: React.FC<FileInfoProps> = ({
  children,
  name,
  description,
  disabled,
  statusLabel,
  isLoading,
  isReplacing,
  url,
  infoText,
  isError,
  isWarning,
  actions = {},
  icons = {},
  tooltips = {},
}) => {
  const openInNewTab = (targetUrl: string) => {
    try {
      const a = document.createElement("a");
      a.href = targetUrl;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (e) {
      window.open(targetUrl, "_blank", "noopener,noreferrer");
    }
  };

  const handlePreviewNewTab = () => {
    if (actions.previewNewTab) {
      actions.previewNewTab();
    } else if (url) {
      openInNewTab(url);
    }
  };

  return (
    <>
      <Wrapper
        $isReplacing={isReplacing}
        $isError={isError}
        $isWarning={isWarning}
      >
        {/* spinner */}
        {isLoading && <Spinner />}
        {!isLoading && !disabled && (
          <>
            {/* preview in modal */}
            {icons.previewModal && actions.previewModal && (
              <IconButton
                iconProps={{
                  icon: "eye",
                  size: "2xs",
                }}
                tooltipProps={{ title: tooltips.previewModal }}
                data-testid="preview-modal"
                onClick={actions.previewModal}
                sx={{ marginTop: "-0.2rem" }}
              />
            )}

            {/* preview in new tab */}
            {icons.previewNewTab && (actions.previewNewTab || url) && (
              <IconButton
                iconProps={{
                  icon: "previewNewTab",
                  size: "2xs",
                }}
                tooltipProps={{ title: tooltips.previewNewTab }}
                data-testid="preview-new-tab"
                onClick={handlePreviewNewTab}
                sx={{ marginTop: "-0.2rem" }}
              />
            )}
          </>
        )}

        {/* file name & description */}
        <FileDetails>
          <Typography
            variant="bodyLarge"
            color={color.foreground.default}
            maxLines={2}
          >
            {name}
          </Typography>

          {description &&
            (typeof description === "string" ? (
              <Typography variant="bodySmall" color={color.neutral[400]}>
                {description}
              </Typography>
            ) : (
              description
            ))}
        </FileDetails>

        {/* actions */}

        {isLoading && icons.cancel && actions.cancel ? (
          <IconButton
            iconProps={{
              icon: "x",
              size: "2xs",
            }}
            tooltipProps={{ title: tooltips.cancel }}
            data-testid="cancel"
            onClick={actions.cancel}
            sx={{ marginTop: "-0.2rem" }}
          />
        ) : (
          children ||
          (!disabled && (
            <>
              {/* status label */}
              {statusLabel && (
                <StatusLabel variant="bodySmall">{statusLabel}</StatusLabel>
              )}

              <IconWrapper>
                {/* replace */}
                {icons.replace && actions.replace && !isReplacing && (
                  <IconButton
                    iconProps={{
                      icon: "replace",
                      size: "2xs",
                    }}
                    tooltipProps={{ title: tooltips.replace }}
                    data-testid="replace-file"
                    onClick={actions.replace}
                    sx={{ marginTop: "-0.2rem" }}
                  />
                )}

                {/* download */}
                {icons.download && actions.download && !isReplacing && (
                  <IconButton
                    iconProps={{
                      icon: "download",
                      size: "2xs",
                    }}
                    tooltipProps={{ title: tooltips.download }}
                    data-testid="download-file"
                    onClick={actions.download}
                    sx={{ marginTop: "-0.2rem" }}
                  />
                )}

                {/* delete */}
                {icons.delete && actions.delete && (
                  <IconButton
                    iconProps={{
                      icon: "delete",
                      size: "2xs",
                    }}
                    tooltipProps={{ title: tooltips.delete }}
                    data-testid="delete-file"
                    onClick={actions.delete}
                    sx={{ marginTop: "-0.2rem" }}
                  />
                )}
              </IconWrapper>
            </>
          ))
        )}
      </Wrapper>
      {infoText && <StyledInfoText>{infoText}</StyledInfoText>}
    </>
  );
};

interface WrapperProps {
  $isReplacing?: boolean;
  $isError?: boolean;
  $isWarning?: boolean;
}

const getBorderColor = ({
  $isReplacing,
  $isError,
  $isWarning,
}: WrapperProps) => {
  if ($isReplacing) {
    return color.blue[300];
  }
  if ($isError) {
    return color.border.danger;
  }
  if ($isWarning) {
    return color.border.warning;
  }
  return color.border.default;
};

const Wrapper = styled.div<WrapperProps>`
  padding: 1rem;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-direction: row;
  gap: 0.5rem;
  background-color: ${color.neutral[100]};
  border-style: solid;
  border-radius: 0.75rem;
  border-width: ${({ $isReplacing, $isError, $isWarning }) =>
    $isReplacing || $isError || $isWarning ? "2px" : "1px"};
  border-color: ${getBorderColor};
`;

const FileDetails = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: fit-content;
  overflow: hidden;
  word-wrap: break-word;
`;

const StatusLabel = styled(Typography)`
  color: ${color.green[400]};
  margin: 0.25rem 0 0.25rem 0.5rem;
  text-transform: uppercase;
`;

const IconWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const StyledInfoText = styled(Typography).attrs({
  variant: "bodySmall",
  color: color.neutral[400],
})`
  margin-top: 0.25rem;
`;
